// Standard "optimal input count" finesse model (as used by trainers like
// TETR.IO/jstris): for each locked piece, optimal = rotation presses +
// horizontal presses + 1 hard drop, computed against an open board. Movement
// is counted in discrete key *presses*, not cells — a DAS charge into a wall
// is one press regardless of distance, matching how players actually count
// finesse, which is why "move to wall then tap back" can beat direct taps.

import { BOARD_WIDTH, SHAPE_TABLE, type PieceType } from './constants';
import type { Orientation } from './game';

const SPAWN_X = 4;

function footprint(piece: PieceType, orientation: Orientation): { min: number; max: number } {
  const xs = SHAPE_TABLE[piece][orientation].map(([dx]) => dx);
  return { min: Math.min(...xs), max: Math.max(...xs) };
}

/** 0, 1, or 2 orientation steps away from spawn — but with CW/CCW/180 all
 * bindable to a single press each, every non-zero orientation costs exactly 1. */
export function optimalRotationInputs(orientation: Orientation): number {
  return orientation === 0 ? 0 : 1;
}

export function optimalHorizontalInputs(piece: PieceType, orientation: Orientation, finalX: number): number {
  const { min, max } = footprint(piece, orientation);
  const leftWallX = -min;
  const rightWallX = BOARD_WIDTH - 1 - max;
  const direct = Math.abs(finalX - SPAWN_X);
  const viaLeftWall = 1 + Math.abs(finalX - leftWallX);
  const viaRightWall = 1 + Math.abs(finalX - rightWallX);
  return Math.min(direct, viaLeftWall, viaRightWall);
}

/** Minimal key presses to place `piece` at `orientation`/`finalX` and lock it. */
export function optimalInputs(piece: PieceType, orientation: Orientation, finalX: number): number {
  return optimalRotationInputs(orientation) + optimalHorizontalInputs(piece, orientation, finalX) + 1;
}

export function isFault(actualInputs: number, piece: PieceType, orientation: Orientation, finalX: number): boolean {
  return actualInputs > optimalInputs(piece, orientation, finalX);
}

export class FinesseTracker {
  pieces = 0;
  faults = 0;

  record(fault: boolean): void {
    this.pieces += 1;
    if (fault) this.faults += 1;
  }

  get percent(): number {
    if (this.pieces === 0) return 100;
    return ((this.pieces - this.faults) / this.pieces) * 100;
  }

  reset(): void {
    this.pieces = 0;
    this.faults = 0;
  }
}
